/*
 * @Author: Nemo
 * @Description: HTTP请求配置和拦截器
 * @Date: 2024-11-22 10:07:33
 * @LastEditors: Nemo
 * @FilePath: /rhcn-checkout-web/src/utils/http/index.js
 */
import axios from 'axios';

import { deviceFingerprintManager } from '@/plugins/eventTrackManager/deviceFingerprintManager';

import requestTracker from './requestTracker';

// axios 配置
const http = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://acq.onerway.com',
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
  transformRequest: [
    function formatData(data, headers) {
      if (headers['Content-type'] === 'multipart/form-data') {
        return data;
      }
      return JSON.stringify(data);
    },
  ],
});

/**
 * 从URL获取订单key
 * @returns {string} 订单key
 */
function getOrderKeyFromUrl() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('key') || '';
  } catch (error) {
    return '';
  }
}

export function initHttpInterceptors() {
  // 请求拦截器
  http.interceptors.request.use(
    (config) => {
      // 生成并附加请求ID进行追踪
      config.requestId = requestTracker.trackRequest(config);

      // 检查请求地址是否包含 'txn/local/confirmPayment'
      if (config.url && (config.url.includes('txn/confirmPayment') || config.url.includes('txn/local/confirmPayment'))) {
        // 获取当前语言
        let selectedLanguage = (navigator.language || navigator.languages[0]).slice(0, 2);
        // 特殊处理 zh-TW 和 zh-HK 转为 tw
        const browserLanguage = navigator.language.toLowerCase();
        if (browserLanguage === 'zh-tw' || browserLanguage === 'zh-hk') {
          selectedLanguage = 'hk';
        }
        // 将 selected-language 添加到请求头,后端使用这个做重试处理
        config.headers['selected-language'] = selectedLanguage;
      }
      // 开发环境下，如果请求是 post,put,patch,则打印数据体，方便调试
      if (process.env.NODE_ENV === 'development') {
        const { method } = config;
        if (method === 'post' || method === 'put' || method === 'patch') {
          console.log('config.data', config.data);
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // 响应拦截器
  http.interceptors.response.use(
    (res) => {
      const requestId = res.config.requestId;
      if (requestId) {
        requestTracker.markCompleted(requestId, res);

        // 如果HTTP状态码非200，发送埋点
        if (res.status !== 200) {
          const request = requestTracker.getRequest(requestId);
          try {
            deviceFingerprintManager.send({
              e: 'api_error',
              lmo: res.status.toString(),
              r: JSON.stringify({
                requestId,
                api: request?.url,
                method: request?.method,
                errorType: 'http_error',
                httpStatus: res.status,
                statusText: res.statusText,
                duration: request?.duration,
                rawResponse: JSON.stringify(res.data).substring(0, 1000),
              }),
              k: getOrderKeyFromUrl(),
            });
          } catch (err) {
            console.error('发送API错误埋点失败:', err);
          }
        }
      }

      return res.data;
    },
    (error) => {
      const config = error.config || {};
      const requestId = config.requestId;

      if (requestId) {
        requestTracker.markFailed(requestId, error);
        const request = requestTracker.getRequest(requestId);

        // 判断错误类型
        let errorType = 'unknown_error';
        let errorCode = 'UNKNOWN';

        if (error.code === 'ECONNABORTED') {
          errorType = 'timeout';
          errorCode = 'TIMEOUT';
        } else if (error.code === 'ERR_NETWORK' || !error.response) {
          errorType = 'network_error';
          errorCode = error.code || 'NETWORK_ERROR';
        } else if (error.response) {
          errorType = 'http_error';
          errorCode = error.response.status.toString();
        }

        // 发送错误埋点
        try {
          deviceFingerprintManager.send({
            e: 'api_error',
            lmo: errorCode,
            r: JSON.stringify({
              requestId,
              api: request?.url,
              method: request?.method,
              errorType,
              errorCode,
              errorMessage: error.message,
              duration: request?.duration,
              rawResponse: error.response
                ? JSON.stringify(error.response.data).substring(0, 1000) : null,
              networkError: !error.response ? {
                message: error.message,
                code: error.code,
              } : null,
            }),
            k: getOrderKeyFromUrl(),
          });
        } catch (err) {
          console.error('发送API错误埋点失败:', err);
        }
      }

      if (error && error.response) {
        switch (error.response.status) {
          // 401 token失效
          case 401:
            // router.push('/login');
            break;
          // 403 服务器拒绝访问
          case 403:
            // router.push('/error/403');
            break;
          // 404 访问的资源不存在
          case 404:
            // router.push('/error/404');
            break;
          // 500 服务器错误
          case 500:
            // router.push('/error/500');
            break;
          default:
            // router.push('/error/404');
            break;
        }
        return Promise.reject(error);
      }
      return Promise.reject(error);
    },
  );

  // 设置超时请求处理回调
  requestTracker.onLongPending = (requests) => {
    requests.forEach(request => {
      try {
        deviceFingerprintManager.send({
          e: 'api_error',
          lmo: 'LONG_PENDING',
          r: JSON.stringify({
            requestId: request.requestId,
            api: request.url,
            method: request.method,
            errorType: 'long_pending',
            duration: request.duration,
            message: '请求超过60秒未收到响应',
          }),
          k: getOrderKeyFromUrl(),
        });
      } catch (err) {
        console.error('发送长时间未响应埋点失败:', err);
      }
    });
  };
}

export default http;
